import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Shield, Globe, Settings, Ban } from 'lucide-react';
import { formatNumber, formatPercent } from '@/lib/format';
import { useNow } from '@/lib/hooks';

const REFETCH = 10_000;

export function AdGuardWidget() {
  const now = useNow(1_000);
  const q = useQuery({
    queryKey: ['adguard'],
    queryFn: endpoints.integrations.adguard,
    refetchInterval: REFETCH,
  });
  const data = q.data?.adguard;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-3.5 w-3.5" />
          AdGuard Home
        </CardTitle>
        {data && (
          <span className="text-xs text-muted-foreground">{data.version}</span>
        )}
      </CardHeader>
      <CardBody>
        {!data ? (
          <div className="text-sm text-muted-foreground">
            {q.isLoading ? 'loading…' : 'unavailable (check ADGUARD_BASE_URL env)'}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-sm">
              <Tile label="24h queries" value={formatNumber(data.totalQueries)} icon={Globe} />
              <Tile label="blocked" value={formatNumber(data.blockedQueries)} icon={Ban} />
              <Tile label="block rate" value={formatPercent(data.blockedPercent, 1)} icon={Shield} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                Top blocked
              </div>
              {data.topBlocked.length === 0 ? (
                <div className="text-xs text-muted-foreground">no blocks in window</div>
              ) : (
                <ul className="space-y-0.5">
                  {data.topBlocked.map((b) => (
                    <li
                      key={b.domain}
                      className="flex items-center justify-between text-xs font-mono"
                    >
                      <span className="truncate text-foreground/80">{b.domain}</span>
                      <span className="text-muted-foreground tabular-nums">
                        {formatNumber(b.count)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-2">
              <span className="flex items-center gap-1.5">
                <Settings className="h-3 w-3" />
                {data.numRules} filtering rule(s)
              </span>
              <span>{q.data?.lastUpdated ? relTime(q.data.lastUpdated, now) : '—'}</span>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Tile({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: typeof Globe;
}) {
  return (
    <div className="rounded-md border border-border p-2">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="text-lg font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function relTime(iso: string, now: number): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const diff = Math.max(0, Math.floor((now - t) / 1000));
  if (diff === 0) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}
